import "@saleor/macaw-ui-next/style";
import "./index.css";
// Windows 대시보드 글꼴 'Pretendard Dashboard' 선언. 글꼴 파일은 실제로 쓰일 때만 받는다 (2026-10-19 KST 추가)
import "./styles/pretendard-dashboard.css";

import { ApolloProvider } from "@apollo/client";
// 무통장입금 내역 (2026-08-10 KST 추가)
import { bankDepositsPath } from "@dashboard/bankDeposits/urls";
import { bankTransferSettingsPath } from "@dashboard/bankTransferSettings/urls";
// 우편번호 API 설정 (2026-08-10 KST 추가)
import { bulkPurchaseSettingsPath } from "@dashboard/bulkPurchaseSettings/urls";
import { businessInfoSettingsPath } from "@dashboard/businessInfoSettings/urls";
import { cashReceiptsPath } from "@dashboard/cashReceipts/urls";
import { history, Route, Router } from "@dashboard/components/Router";
// 디자인 관리 (2026-08-18 KST 추가)
import { designSettingsPath } from "@dashboard/designSettings/urls";
import { AppExtensionPopupProvider } from "@dashboard/extensions/components/AppExtensionContext/AppExtensionContextProvider";
import { ExtensionsPaths, extensionsSection } from "@dashboard/extensions/urls";
import { PermissionEnum } from "@dashboard/graphql";
import useAppState from "@dashboard/hooks/useAppState";
// 메시지(SMS/카카오 알림톡) 설정 (2026-08-09 KST 추가)
import { deliveryTrackerSettingsPath, tossSettingsPath } from "@dashboard/integrationSettings/urls";
// 카카오 채널톡 설정 (2026-10-20 KST 추가)
import { kakaoChannelSettingsPath } from "@dashboard/kakaoChannelSettings/urls";
// 킴스온 이전 (2026-11-02 KST 추가)
import { kimsonImportPath } from "@dashboard/kimsonImport/urls";
import { SaleorProvider } from "@dashboard/legacy-sdk";
import { messageSettingsPath } from "@dashboard/messageSettings/urls";
import { pageListPath } from "@dashboard/modeling/urls";
import { modelTypesPath } from "@dashboard/modelTypes/urls";
// 반품 관리 (2026-08-12 KST 추가)
import { orderReturnsPath } from "@dashboard/orderReturns/urls";
import { orderTrackingPath } from "@dashboard/orderTracking/urls";
import { paymentMethodSettingsPath } from "@dashboard/paymentMethodSettings/urls";
// 포인트 설정 (2026-08-25 KST 추가)
import { pointSettingsPath } from "@dashboard/pointSettings/urls";
import { postcodeSettingsPath } from "@dashboard/postcodeSettings/urls";
import { refundsSettingsPath } from "@dashboard/refundsSettings/urls";
// 재입고(상품 판매) 알림 (2026-11-01 KST 추가)
import { restockAlarmsPath } from "@dashboard/restockAlarms/urls";
// 구매후기 목록 (2026-08-25 KST 추가)
import { reviewsPath } from "@dashboard/reviews/urls";
// 매출 통계 (2026-09-05 KST 추가)
import { salesStatsPath } from "@dashboard/salesStats/urls";
// 도서/산간 지역 배송비 (2026-08-24 KST 추가)
import { shippingRegionsPath } from "@dashboard/shippingRegions/urls";
// 기본 배송 설정 (2026-08-24 KST 추가)
import { shippingSettingsPath } from "@dashboard/shippingSettings/urls";
// SNS 로그인 설정 (2026-08-07 KST 추가)
import { snsLoginSettingsPath } from "@dashboard/snsLoginSettings/urls";
import { structuresListPath } from "@dashboard/structures/urls";
// 공급사 관리 (2026-08-26 KST 추가)
import { suppliersPath } from "@dashboard/suppliers/urls";
import { ThemeProvider } from "@dashboard/theme";
// 쿠폰 자동발급 설정 (2026-09-06 KST 추가)
import { voucherAutoIssuePath } from "@dashboard/voucherAutoIssue/urls";
import { ThemeProvider as LegacyThemeProvider } from "@saleor/macaw-ui";
import { lazy, StrictMode, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { ErrorBoundary } from "react-error-boundary";
import TagManager from "react-gtm-module";
import { useIntl } from "react-intl";
import { Redirect, Switch } from "react-router-dom";

import { attributeSection } from "./attributes/urls";
import AuthProvider from "./auth/AuthProvider";
import LoginLoading from "./auth/components/LoginLoading/LoginLoading";
import SectionRoute from "./auth/components/SectionRoute";
import { useAuthRedirection } from "./auth/hooks/useAuthRedirection";
// 묶음상품 그룹 (2026-10-15 KST 추가)
import { bundleShippingGroupsPath } from "./bundleShippingGroups/urls";
import { channelsSection } from "./channels/urls";
import AppLayout from "./components/AppLayout";
import useAppChannel, { AppChannelProvider } from "./components/AppLayout/AppChannelContext";
import { DevModeProvider } from "./components/DevModePanel/DevModeProvider";
import ErrorPage from "./components/ErrorPage";
import ExitFormDialogProvider from "./components/Form/ExitFormDialogProvider";
import { legacyRedirects } from "./components/LegacyRedirects";
import { LocaleProvider } from "./components/Locale";
import { NavigatorSearchProvider } from "./components/NavigatorSearch/NavigatorSearchProvider";
import { NotificationProvider } from "./components/notifications";
import { ProductAnalytics } from "./components/ProductAnalytics";
import { SavebarRefProvider } from "./components/Savebar/SavebarRefContext";
import { ShopProvider } from "./components/Shop";
import { WindowTitle } from "./components/WindowTitle";
import { GTM_ID } from "./config";
import { getConfigMenuItemsPermissions } from "./configuration/utils";
import AppStateProvider from "./containers/AppState";
import BackgroundTasksProvider from "./containers/BackgroundTasks";
import { FeatureFlagsProviderWithUser } from "./featureFlags/FeatureFlagsProvider";
import { giftCardsSectionUrlName } from "./giftCards/urls";
import { apolloClient, saleorClient } from "./graphql/client";
import { useLocationState } from "./hooks/useLocationState";
import { commonMessages } from "./intl";
import { NotFound } from "./NotFound";
import { errorTracker } from "./services/errorTracking";
import { paletteOverrides, themeOverrides } from "./themeOverrides";
import { warehouseSection } from "./warehouses/urls";
import { OnboardingProvider } from "./welcomePage/WelcomePageOnboarding/onboardingContext";

// Lazy-loaded page sections for code splitting
const AttributeSection = lazy(() => import("./attributes"));
const Auth = lazy(() => import("./auth"));
const CategorySection = lazy(() => import("./categories"));
const ChannelsSection = lazy(() => import("./channels"));
const CollectionSection = lazy(() => import("./collections"));
const CustomerSection = lazy(() =>
  import("./customers").then(m => ({ default: m.CustomerSection })),
);
const DiscountSection = lazy(() => import("./discounts"));
const ExtensionsSection = lazy(() =>
  import("./extensions").then(m => ({ default: m.ExtensionsSection })),
);
const GiftCardSection = lazy(() => import("./giftCards"));
const PageSection = lazy(() => import("./modeling"));
const PageTypesSection = lazy(() => import("./modelTypes"));
const OrdersSection = lazy(() => import("./orders"));
const PermissionGroupSection = lazy(() => import("./permissionGroups"));
const ProductSection = lazy(() => import("./products"));
const ProductTypesSection = lazy(() => import("./productTypes"));
const SearchSection = lazy(() => import("./search"));
const ShippingSection = lazy(() => import("./shipping"));
const SiteSettingsSection = lazy(() => import("./siteSettings"));
const StaffSection = lazy(() => import("./staff"));
const NavigationSection = lazy(() => import("./structures"));
const TaxesSection = lazy(() => import("./taxes"));
const TranslationsSection = lazy(() => import("./translations"));
const WarehouseSection = lazy(() => import("./warehouses"));
const ConfigurationSection = lazy(() => import("./configuration"));
const WelcomePage = lazy(() => import("./welcomePage").then(m => ({ default: m.WelcomePage })));
const HomePage = lazy(() => import("./home/HomePage").then(m => ({ default: m.HomePage })));
const RefundsSettingsRoute = lazy(() =>
  import("./refundsSettings/route").then(m => ({ default: m.RefundsSettingsRoute })),
);
// 무통장입금 계좌 설정 (2026-08-06 KST 추가)
const BankTransferSettingsRoute = lazy(() =>
  import("./bankTransferSettings/route").then(m => ({ default: m.BankTransferSettingsRoute })),
);
// 현금영수증 관리 (2026-08-06 KST 추가)
const CashReceiptsRoute = lazy(() =>
  import("./cashReceipts/route").then(m => ({ default: m.CashReceiptsRoute })),
);
// 결제수단 노출 설정 (2026-08-06 KST 추가)
const PaymentMethodSettingsRoute = lazy(() =>
  import("./paymentMethodSettings/route").then(m => ({ default: m.PaymentMethodSettingsRoute })),
);
// 사업자 정보 설정 (2026-08-07 KST 추가)
const BusinessInfoSettingsRoute = lazy(() =>
  import("./businessInfoSettings/route").then(m => ({ default: m.BusinessInfoSettingsRoute })),
);
// SNS 로그인 설정 (2026-08-07 KST 추가)
const SnsLoginSettingsRoute = lazy(() =>
  import("./snsLoginSettings/route").then(m => ({ default: m.SnsLoginSettingsRoute })),
);
// 메시지(SMS/카카오 알림톡) 설정 (2026-08-09 KST 추가)
const MessageSettingsRoute = lazy(() =>
  import("./messageSettings/route").then(m => ({ default: m.MessageSettingsRoute })),
);
// 우편번호 API 설정 (2026-08-10 KST 추가)
const PostcodeSettingsRoute = lazy(() =>
  import("./postcodeSettings/route").then(m => ({ default: m.PostcodeSettingsRoute })),
);
// 카카오 채널톡 설정 (2026-10-20 KST 추가)
const KakaoChannelSettingsRoute = lazy(() =>
  import("./kakaoChannelSettings/route").then(m => ({ default: m.KakaoChannelSettingsRoute })),
);
// 대량구매 설정 (2026-09-01 KST 추가)
const BulkPurchaseSettingsRoute = lazy(() =>
  import("./bulkPurchaseSettings/route").then(m => ({ default: m.BulkPurchaseSettingsRoute })),
);
// 토스페이먼츠 / 배송조회 연동 키 설정 (2026-08-30 KST 추가)
const TossSettingsRoute = lazy(() =>
  import("./integrationSettings/route").then(m => ({ default: m.TossSettingsRoute })),
);
const DeliveryTrackerSettingsRoute = lazy(() =>
  import("./integrationSettings/route").then(m => ({ default: m.DeliveryTrackerSettingsRoute })),
);
// 포인트 설정 (2026-08-25 KST 추가)
const PointSettingsRoute = lazy(() =>
  import("./pointSettings/route").then(m => ({ default: m.PointSettingsRoute })),
);
// 쿠폰 자동발급 설정 (2026-09-06 KST 추가)
const VoucherAutoIssueRoute = lazy(() =>
  import("./voucherAutoIssue/route").then(m => ({ default: m.VoucherAutoIssueRoute })),
);
// 구매후기 목록 (2026-08-25 KST 추가)
const ReviewsRoute = lazy(() => import("./reviews/route").then(m => ({ default: m.ReviewsRoute })));
// 재입고(상품 판매) 알림 (2026-11-01 KST 추가)
const RestockAlarmsRoute = lazy(() =>
  import("./restockAlarms/route").then(m => ({ default: m.RestockAlarmsRoute })),
);
// 킴스온 이전 (2026-11-02 KST 추가)
const KimsonImportRoute = lazy(() =>
  import("./kimsonImport/route").then(m => ({ default: m.KimsonImportRoute })),
);
// 공급사 관리 (2026-08-26 KST 추가)
const SuppliersRoute = lazy(() =>
  import("./suppliers/route").then(m => ({ default: m.SuppliersRoute })),
);
// 묶음상품 그룹 (2026-10-15 KST 추가)
const BundleShippingGroupsRoute = lazy(() =>
  import("./bundleShippingGroups/route").then(m => ({ default: m.BundleShippingGroupsRoute })),
);
// 기본 배송 설정 (2026-08-24 KST 추가)
const ShippingSettingsRoute = lazy(() =>
  import("./shippingSettings/route").then(m => ({ default: m.ShippingSettingsRoute })),
);
// 도서/산간 지역 배송비 (2026-08-24 KST 추가)
const ShippingRegionsRoute = lazy(() =>
  import("./shippingRegions/route").then(m => ({ default: m.ShippingRegionsRoute })),
);
// 반품 관리 (2026-08-12 KST 추가)
const OrderReturnsRoute = lazy(() =>
  import("./orderReturns/route").then(m => ({ default: m.OrderReturnsRoute })),
);
// 송장번호 일괄등록 (2026-10-29 KST 추가)
const OrderTrackingRoute = lazy(() =>
  import("./orderTracking/route").then(m => ({ default: m.OrderTrackingRoute })),
);
// 무통장입금 내역 (2026-08-10 KST 추가)
const BankDepositsRoute = lazy(() =>
  import("./bankDeposits/route").then(m => ({ default: m.BankDepositsRoute })),
);
// 매출 통계 (2026-09-05 KST 추가)
const SalesStatsRoute = lazy(() =>
  import("./salesStats/route").then(m => ({ default: m.SalesStatsRoute })),
);
// 디자인 관리 (2026-08-18 KST 추가)
const DesignSettingsRoute = lazy(() =>
  import("./designSettings/route").then(m => ({ default: m.DesignSettingsRoute })),
);

if (GTM_ID) {
  TagManager.initialize({ gtmId: GTM_ID });
}

errorTracker.init(history);

/*
  Handle legacy theming toggle. Since we use new and old macaw,
  we need to handle both theme swticher for a while.
*/
const handleLegacyTheming = (): void => {
  const activeTheme = localStorage.getItem("activeMacawUITheme");

  if (activeTheme === "defaultDark") {
    localStorage.setItem("macaw-ui-theme", "dark");

    return;
  }

  localStorage.setItem("macaw-ui-theme", "light");
};

handleLegacyTheming();

const App = (): JSX.Element => (
  <SaleorProvider client={saleorClient}>
    <ApolloProvider client={apolloClient}>
      <Router>
        {/* @ts-expect-error legacy types */}
        <LegacyThemeProvider overrides={themeOverrides} palettes={paletteOverrides}>
          <ThemeProvider>
            <LocaleProvider>
              <NotificationProvider>
                <BackgroundTasksProvider>
                  <AppStateProvider>
                    <AuthProvider>
                      <ProductAnalytics>
                        <ShopProvider>
                          <AppChannelProvider>
                            <ExitFormDialogProvider>
                              <DevModeProvider>
                                <NavigatorSearchProvider>
                                  <SavebarRefProvider>
                                    <FeatureFlagsProviderWithUser>
                                      <OnboardingProvider>
                                        <Routes />
                                      </OnboardingProvider>
                                    </FeatureFlagsProviderWithUser>
                                  </SavebarRefProvider>
                                </NavigatorSearchProvider>
                              </DevModeProvider>
                            </ExitFormDialogProvider>
                          </AppChannelProvider>
                        </ShopProvider>
                      </ProductAnalytics>
                    </AuthProvider>
                  </AppStateProvider>
                </BackgroundTasksProvider>
              </NotificationProvider>
            </LocaleProvider>
          </ThemeProvider>
        </LegacyThemeProvider>
      </Router>
    </ApolloProvider>
  </SaleorProvider>
);
const Routes = () => {
  const intl = useIntl();
  const [, dispatchAppState] = useAppState();
  const { authenticated, authenticating } = useAuthRedirection();
  const { channel } = useAppChannel(false);
  const channelLoaded = typeof channel !== "undefined";
  const homePageLoaded = channelLoaded && authenticated;
  const homePageLoading = (authenticated && !channelLoaded) || authenticating;
  const { isAppPath } = useLocationState();

  return (
    <>
      <WindowTitle title={intl.formatMessage(commonMessages.dashboard)} />
      {homePageLoaded ? (
        <AppExtensionPopupProvider>
          <AppLayout fullSize={isAppPath}>
            <ErrorBoundary
              onError={e => {
                const errorId = errorTracker.captureException(e);

                dispatchAppState({
                  payload: {
                    error: "unhandled",
                    errorId,
                  },
                  type: "displayError",
                });
              }}
              fallbackRender={({ resetErrorBoundary }) => (
                <ErrorPage onBack={resetErrorBoundary} onRefresh={() => window.location.reload()} />
              )}
            >
              <Suspense fallback={<LoginLoading />}>
                <Switch>
                  {legacyRedirects}
                  <SectionRoute exact path="/" component={WelcomePage} />
                  <SectionRoute exact path="/home" component={HomePage} />
                  <SectionRoute exact path="/home/widget/:extensionId" component={HomePage} />
                  <SectionRoute exact path="/home/widgets" component={HomePage} />
                  <SectionRoute
                    permissions={[
                      PermissionEnum.MANAGE_PRODUCTS,
                      PermissionEnum.MANAGE_ORDERS,
                      PermissionEnum.MANAGE_PAGES,
                      PermissionEnum.MANAGE_PRODUCT_TYPES_AND_ATTRIBUTES,
                    ]}
                    matchPermission="any"
                    path="/search"
                    component={SearchSection}
                  />
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_PRODUCTS]}
                    path="/categories"
                    component={CategorySection}
                  />
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_PRODUCTS]}
                    path="/collections"
                    component={CollectionSection}
                  />
                  <SectionRoute
                    permissions={[
                      PermissionEnum.MANAGE_USERS,
                      PermissionEnum.MANAGE_ORDERS,
                      PermissionEnum.MANAGE_STAFF,
                    ]}
                    matchPermission="any"
                    path="/customers"
                    component={CustomerSection}
                  />
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_GIFT_CARD]}
                    path={giftCardsSectionUrlName}
                    component={GiftCardSection}
                  />
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_DISCOUNTS]}
                    path="/discounts"
                    component={DiscountSection}
                  />
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_PAGES]}
                    path={pageListPath}
                    component={PageSection}
                  />
                  <SectionRoute
                    permissions={[
                      PermissionEnum.MANAGE_PAGES,
                      PermissionEnum.MANAGE_PAGE_TYPES_AND_ATTRIBUTES,
                    ]}
                    path={modelTypesPath}
                    component={PageTypesSection}
                    matchPermission="any"
                  />
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_ORDERS]}
                    path="/orders"
                    component={OrdersSection}
                  />
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_PRODUCTS]}
                    path="/products"
                    component={ProductSection}
                  />
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_PRODUCT_TYPES_AND_ATTRIBUTES]}
                    path="/product-types"
                    component={ProductTypesSection}
                  />
                  <SectionRoute path="/staff" component={StaffSection} />
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_STAFF]}
                    path="/permission-groups"
                    component={PermissionGroupSection}
                  />
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_SETTINGS]}
                    path="/site-settings"
                    component={SiteSettingsSection}
                  />
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_SETTINGS]}
                    path={refundsSettingsPath}
                    component={RefundsSettingsRoute}
                  />
                  {/* 무통장입금 계좌 설정 (2026-08-06 KST 추가) */}
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_SETTINGS]}
                    path={bankTransferSettingsPath}
                    component={BankTransferSettingsRoute}
                  />
                  {/* 결제수단 노출 설정 (2026-08-06 KST 추가) */}
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_SETTINGS]}
                    path={paymentMethodSettingsPath}
                    component={PaymentMethodSettingsRoute}
                  />
                  {/* 사업자 정보 설정 (2026-08-07 KST 추가) */}
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_SETTINGS]}
                    path={businessInfoSettingsPath}
                    component={BusinessInfoSettingsRoute}
                  />
                  {/* SNS 로그인 설정 (2026-08-07 KST 추가) */}
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_PLUGINS]}
                    path={snsLoginSettingsPath}
                    component={SnsLoginSettingsRoute}
                  />
                  {/* 메시지(SMS/카카오 알림톡) 설정 (2026-08-09 KST 추가) */}
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_SETTINGS]}
                    path={messageSettingsPath}
                    component={MessageSettingsRoute}
                  />
                  {/* 우편번호 API 설정 (2026-08-10 KST 추가) */}
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_SETTINGS]}
                    path={postcodeSettingsPath}
                    component={PostcodeSettingsRoute}
                  />
                  {/* 카카오 채널톡 설정 (2026-10-20 KST 추가) */}
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_SETTINGS]}
                    path={kakaoChannelSettingsPath}
                    component={KakaoChannelSettingsRoute}
                  />
                  {/* 대량구매 설정 (2026-09-01 KST 추가) */}
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_SETTINGS]}
                    path={bulkPurchaseSettingsPath}
                    component={BulkPurchaseSettingsRoute}
                  />
                  {/* 토스페이먼츠 / 배송조회 연동 키 설정 (2026-08-30 KST 추가) */}
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_SETTINGS]}
                    path={tossSettingsPath}
                    component={TossSettingsRoute}
                  />
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_SETTINGS]}
                    path={deliveryTrackerSettingsPath}
                    component={DeliveryTrackerSettingsRoute}
                  />
                  {/* 포인트 설정 (2026-08-25 KST 추가) */}
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_SETTINGS]}
                    path={pointSettingsPath}
                    component={PointSettingsRoute}
                  />
                  {/* 쿠폰 자동발급 설정 (2026-09-06 KST 추가) */}
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_SETTINGS, PermissionEnum.MANAGE_DISCOUNTS]}
                    path={voucherAutoIssuePath}
                    component={VoucherAutoIssueRoute}
                  />
                  {/* 기본 배송 설정 (2026-08-24 KST 추가) */}
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_SETTINGS]}
                    path={shippingSettingsPath}
                    component={ShippingSettingsRoute}
                  />
                  {/* 도서/산간 지역 배송비 (2026-08-24 KST 추가) */}
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_SETTINGS]}
                    path={shippingRegionsPath}
                    component={ShippingRegionsRoute}
                  />
                  {/* 현금영수증 관리 (2026-08-06 KST 추가) */}
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_ORDERS]}
                    path={cashReceiptsPath}
                    component={CashReceiptsRoute}
                  />
                  {/* 무통장입금 내역 (2026-08-10 KST 추가) */}
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_ORDERS]}
                    path={bankDepositsPath}
                    component={BankDepositsRoute}
                  />
                  {/* 매출 통계 (2026-09-05 KST 추가) */}
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_ORDERS]}
                    path={salesStatsPath}
                    component={SalesStatsRoute}
                  />
                  {/* 송장번호 일괄등록 (2026-10-29 KST 추가) */}
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_ORDERS]}
                    path={orderTrackingPath}
                    component={OrderTrackingRoute}
                  />
                  {/* 반품 관리 (2026-08-12 KST 추가) */}
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_ORDERS]}
                    path={orderReturnsPath}
                    component={OrderReturnsRoute}
                  />
                  {/* 구매후기 목록 (2026-08-25 KST 추가) */}
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_PRODUCTS]}
                    path={reviewsPath}
                    component={ReviewsRoute}
                  />
                  {/* 재입고(상품 판매) 알림 (2026-11-01 KST 추가) */}
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_PRODUCTS]}
                    path={restockAlarmsPath}
                    component={RestockAlarmsRoute}
                  />
                  {/* 킴스온 이전 — 실제 사용은 백엔드가 최고 관리자에게만 허용 (2026-11-02 KST 추가) */}
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_USERS, PermissionEnum.MANAGE_STAFF]}
                    path={kimsonImportPath}
                    component={KimsonImportRoute}
                  />
                  {/* 공급사 관리 (2026-08-26 KST 추가) */}
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_PRODUCTS]}
                    path={suppliersPath}
                    component={SuppliersRoute}
                  />
                  {/* 묶음상품 그룹 (2026-10-15 KST 추가) */}
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_PRODUCTS]}
                    path={bundleShippingGroupsPath}
                    component={BundleShippingGroupsRoute}
                  />

                  {/* 디자인 관리 (2026-08-18 KST 추가) */}
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_SETTINGS]}
                    path={designSettingsPath}
                    component={DesignSettingsRoute}
                  />
                  <SectionRoute path="/taxes" component={TaxesSection} />
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_SHIPPING]}
                    path="/shipping"
                    component={ShippingSection}
                  />
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_TRANSLATIONS]}
                    path="/translations"
                    component={TranslationsSection}
                  />
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_MENUS]}
                    path={structuresListPath}
                    component={NavigationSection}
                  />
                  <SectionRoute
                    permissions={[
                      PermissionEnum.MANAGE_PRODUCT_TYPES_AND_ATTRIBUTES,
                      PermissionEnum.MANAGE_PAGE_TYPES_AND_ATTRIBUTES,
                    ]}
                    path={attributeSection}
                    component={AttributeSection}
                    matchPermission="any"
                  />
                  <SectionRoute
                    permissions={[]}
                    path={extensionsSection}
                    component={ExtensionsSection}
                  />
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_PRODUCTS]}
                    path={warehouseSection}
                    component={WarehouseSection}
                  />
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_CHANNELS]}
                    path={channelsSection}
                    component={ChannelsSection}
                  />
                  <SectionRoute
                    matchPermission="any"
                    permissions={getConfigMenuItemsPermissions(intl)}
                    exact
                    path="/configuration"
                    component={ConfigurationSection}
                  />
                  <Redirect to={ExtensionsPaths.installedExtensions} path={"/apps"} />
                  <Redirect to={ExtensionsPaths.installedExtensions} path="/custom-apps/" />
                  <Redirect to={ExtensionsPaths.installedExtensions} path="/plugins" />
                  <Route component={NotFound} />
                </Switch>
              </Suspense>
            </ErrorBoundary>
          </AppLayout>
        </AppExtensionPopupProvider>
      ) : homePageLoading ? (
        <LoginLoading />
      ) : (
        <Suspense fallback={<LoginLoading />}>
          <Auth />
        </Suspense>
      )}
    </>
  );
};

// 재빌드하면 결과 폴더를 비우고 새 해시 이름으로 청크를 만든다. 빌드 전부터 열려 있던 탭이
// 아직 안 연 화면으로 가면 지워진 옛 청크를 불러오다 실패해 오류 화면이 뜨므로, 새로고침해
// 새 index.html 을 받게 한다. 새로고침 뒤에도 실패하면 무한 반복되지 않게 10초에 한 번만
// 하고, 그때는 기본 동작(오류 화면)에 맡긴다 (2026-09-29 KST 추가)
const CHUNK_RELOAD_KEY = "chunkReloadedAt";

window.addEventListener("vite:preloadError", event => {
  const lastReloadedAt = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? 0);

  if (Date.now() - lastReloadedAt < 10_000) {
    return;
  }

  event.preventDefault();
  sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  window.location.reload();
});

const root = createRoot(document.querySelector("#dashboard-app")!);

// StrictMode is development-only (no effect in production)
// Set VITE_DISABLE_STRICT_MODE=true to disable for testing
const enableStrictMode = import.meta.env.DEV && import.meta.env.VITE_DISABLE_STRICT_MODE !== "true";

root.render(
  enableStrictMode ? (
    <StrictMode>
      <App />
    </StrictMode>
  ) : (
    <App />
  ),
);
